import React, { useEffect, useState } from "react";
import "./Trails.css";
import { db, auth } from "../firebase";
import {
  collection,
  getDocs,
  query,
  where,
  orderBy,
} from "firebase/firestore";

export default function Trails() {
  const [trails, setTrails] = useState([]);
  const [completed, setCompleted] = useState([]);
  const [progressMap, setProgressMap] = useState({});
  const [clueCounts, setClueCounts] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchData() {
      try {
        // 1. Fetch Trails
        const trailsSnap = await getDocs(collection(db, "trails"));
        const loadedTrails = [];

        trailsSnap.forEach((doc) => {
          const data = doc.data();
          if (data.id) delete data.id;
          loadedTrails.push({ ...data, id: doc.id });
        });

        const liveTrails = loadedTrails.filter((t) => t.isLaunched === true);
        setTrails(liveTrails);

        // 2. Count clues
        const clueCountMap = {};
        for (let t of liveTrails) {
          const qClues = query(
            collection(db, "clues"),
            where("trailId", "==", t.id),
            orderBy("order", "asc")
          );

          const snap = await getDocs(qClues);
          clueCountMap[t.id] = snap.size;
        }
        setClueCounts(clueCountMap);

        // 3. Load progress
        if (auth.currentUser) {
          const progressSnap = await getDocs(collection(db, "progress"));
          const progressObj = {};

          progressSnap.forEach((pdoc) => {
            const p = pdoc.data();
            if (p.userId === auth.currentUser.uid) {
              progressObj[p.trailId] = {
                currentIndex: p.currentIndex,
                solvedClues: p.solvedClues || [],
              };
            }
          });

          setProgressMap(progressObj);

          // 4. Load completed trails
          const completedSnap = await getDocs(collection(db, "completedTrails"));
          const completedList = [];

          completedSnap.forEach((cdoc) => {
            const cd = cdoc.data();
            if (cd.userId === auth.currentUser.uid) {
              completedList.push(cd.trailId);
            }
          });

          setCompleted(completedList);
        }
      } catch (err) {
        console.error("Error loading trails:", err);
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, []);

  if (loading) {
    return (
      <main className="trails-page">
        <h2 style={{ textAlign: "center", marginTop: "40px" }}>
          Loading trails...
        </h2>
      </main>
    );
  }

  return (
    <main className="trails-page">
      <section className="trails-header">
        <h1>Browse Trails</h1>
        <p>Choose an adventure and start solving clues across Ireland!</p>
      </section>

      <section className="trails-list">
        {trails.length === 0 && (
          <p style={{ textAlign: "center", width: "100%", color: "#ccc" }}>
            No live trails available right now.
          </p>
        )}

        {trails.map((trail) => {
          const totalClues = clueCounts[trail.id] || 0;
          const solvedClues =
            progressMap[trail.id]?.solvedClues?.length || 0;

          const progressPercent =
            totalClues > 0
              ? Math.round((solvedClues / totalClues) * 100)
              : 0;

          const isCompleted =
            solvedClues === totalClues && totalClues > 0;

          const hasProgress =
            solvedClues > 0 && solvedClues < totalClues;

          return (
            <article
              key={trail.id}
              className="trail-section"
              style={{
                backgroundImage: `url(${
                  trail.bannerImageURL || "/assets/images/banner.jpg"
                })`,
              }}
            >
              <div className="trail-section-overlay" />

              {isCompleted && (
                <div className="trail-completed-badge">✓ Completed</div>
              )}

              <div className="trail-section-content">
                <div className="trail-meta">
                  {trail.location && <span>{trail.location}</span>}
                  {trail.duration && <span>• {trail.duration}</span>}
                  {trail.difficulty && <span>• {trail.difficulty}</span>}
                </div>

                <h2>{trail.name}</h2>

                <p
                  className="trail-desc"
                  style={{ whiteSpace: "pre-line" }}
                >
                  {trail.description || "A mysterious adventure awaits."}
                </p>

                {(progressPercent > 0 || isCompleted) && (
                  <div className="trail-progress-wrapper">
                    <div className="trail-progress-bar">
                      <div
                        className="trail-progress-fill"
                        style={{
                          width: `${isCompleted ? 100 : progressPercent}%`,
                        }}
                      />
                    </div>

                    <p className="trail-progress-text">
                      {isCompleted
                        ? "100% complete"
                        : `${progressPercent}% complete`}
                    </p>
                  </div>
                )}

                <div className="trail-footer">
                  {/* Always show View Trail */}
                  <a
                    href={`/trails/${trail.id}`}
                    className="trail-button"
                  >
                    View Trail
                  </a>

                  {/* If progress > 0% and not complete → show Continue Trail */}
                  {hasProgress && (
                    <a
                      href={`/start-trail/${trail.id}`}
                      className="trail-button continue-btn"
                    >
                      Continue Trail
                    </a>
                  )}
                </div>
              </div>
            </article>
          );
        })}
      </section>
    </main>
  );
}
